import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import openHtmlInNewTab from '@/lib/openHtmlInNewTab';
// Phase 185.e — popup-blocker-proof signed view links
import openSignedDocInNewTab from '@/lib/openSignedDocInNewTab';
import { downloadDocument } from '@/lib/downloadDocument';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DigitalAthleteCard } from '@/components/DigitalAthleteCard';
import StudentAIRecommendationCard from '@/components/StudentAIRecommendationCard';
// Phase 266c — Viral household invite link (parents/relatives via WhatsApp)
import HouseholdInviteLink from '@/components/HouseholdInviteLink';
// Phase 197.b — Longitudinal Trend AI growth-trajectory card
import AthleteTrajectoryCard from '@/components/AthleteTrajectoryCard';
// Phase 262 — Academy Challenge gamification leaderboard
import AcademyChallengeTile from '@/components/dashboard/AcademyChallengeTile';
// Phase 263 — Personal records, streak & badges per-student progression
import StudentStreakWidget from '@/components/dashboard/StudentStreakWidget';
import CoachsTipCard from '@/components/student/CoachsTipCard';
// Phase 249 — Tax-season Year-End Fee Report self-service download
import YearEndReportCard from '@/components/finance/YearEndReportCard';
import DashboardGetAppButton from '@/components/DashboardGetAppButton';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  User, IndianRupee, CalendarCheck, Award, Flame, CheckCircle, XCircle, Clock, CreditCard, Loader2, Download, Star, Eye,
  LayoutDashboard, Lock, Phone, MapPin, Mail, Camera, Droplet, Ruler, Weight, Users, Calendar as CalendarIcon, Sparkles,
  RefreshCw
} from 'lucide-react';
import { bestPhotoFor, resolvePhotoUrl } from '@/lib/photo';
import { toast } from 'sonner';

export default function StudentPortal() {
  const { user } = useAuth();
  // Phase 247.b — URL-driven tab selection. `/student/profile` always shows
  // the profile tab; `/student` (the "Dashboard" sidebar entry) keeps whatever
  // overview tab the user picked, defaulting to `fees` — the single most
  // actionable view for parents on landing. We derive `activeTab` directly
  // from URL + local overview state so there's no setState-in-effect dance.
  const location = useLocation();
  const navigate = useNavigate();
  const isProfileRoute = location.pathname === '/student/profile';
  const [profile, setProfile] = useState(null);
  const [fees, setFees] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [progressCards, setProgressCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [showResetPwd, setShowResetPwd] = useState(false);
  // Phase 261i — Feature flag for the student-portal "Get App" CTA so super
  // admins can run install-surface A/B tests per persona. Default true.
  const showGetAppCta = useFeatureFlag('show_pwa_student_cta', true);
  const [pwdForm, setPwdForm] = useState({ current: '', new_password: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [attMonth, setAttMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedCard, setSelectedCard] = useState(0);
  // Local state for the overview-route tab (fees/attendance/progress/training).
  // Profile is owned by the URL itself.
  const [overviewTab, setOverviewTab] = useState('fees');
  const activeTab = isProfileRoute ? 'profile' : overviewTab;
  const [trainingSessions, setTrainingSessions] = useState(null);

  // Phase 247.b — Tab click owns BOTH the URL transition AND the local
  // overview-state update. Profile clicks navigate; non-profile clicks set
  // local state and (if we're currently on the profile route) navigate back.
  const handleTabChange = useCallback((nextTab) => {
    if (nextTab === 'profile') {
      if (!isProfileRoute) navigate('/student/profile');
      return;
    }
    setOverviewTab(nextTab);
    if (isProfileRoute) navigate('/student');
  }, [isProfileRoute, navigate]);

  useEffect(() => {
    if (activeTab === 'training' && trainingSessions === null) {
      api.get('/student/training-sessions').then(r => setTrainingSessions(r.data || [])).catch(() => setTrainingSessions([]));
    }
  }, [activeTab, trainingSessions]);

  const fetchData = useCallback(async () => {
    try {
      const [profRes, feesRes, attRes, progRes] = await Promise.all([
        api.get('/student/me').catch(() => null),
        api.get('/student/fees').catch(() => ({ data: [] })),
        api.get('/student/attendance').catch(() => ({ data: { records: [], stats: {} } })),
        api.get('/progress-cards').catch(() => ({ data: [] }))
      ]);
      if (profRes) { setProfile(profRes.data); setProfilePhoto(bestPhotoFor(profRes.data)); }
      setFees(feesRes?.data || []);
      setAttendance(attRes?.data || { records: [], stats: {} });
      setProgressCards(progRes?.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (!document.getElementById('rzp-s')) { const s = document.createElement('script'); s.id = 'rzp-s'; s.src = 'https://checkout.razorpay.com/v1/checkout.js'; s.async = true; document.head.appendChild(s); } }, []);

  const autoArmedFeeRef = useRef(false);
  // Phase 254-EmailDeepLink-Parent — when the parent/student lands here from
  // a WhatsApp/email fee reminder carrying `?fee_id=<X>`, auto-launch the
  // Razorpay checkout for that exact fee. One-shot latch via `useRef` (not
  // `useState`) so a mid-flow page refresh can't re-fire the modal. URL
  // query is stripped immediately on match.
  useEffect(() => {
    if (loading || !fees || fees.length === 0) return;
    if (autoArmedFeeRef.current) return;
    const params = new URLSearchParams(location.search);
    const targetId = params.get('fee_id');
    if (!targetId) return;
    autoArmedFeeRef.current = true;
    const target = fees.find((f) => f.id === targetId);
    window.history.replaceState({}, '', location.pathname);
    if (!target) return;
    if (target.status === 'paid') {
      toast.info('This fee is already paid.');
      return;
    }
    if (target.status === 'cancelled') return;
    toast.info(`Launching payment for ${target.fee_type || 'fee'}…`);
    // Defer to next microtask so the auto-arm runs outside React's effect cycle.
    Promise.resolve().then(() => handlePayFee(target));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, fees, location.search]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 MB'); return; }
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { toast.error('Use JPG, PNG, or WEBP'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/students/me/profile-photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfilePhoto(resolvePhotoUrl(res.data.photo_url));
      // Sync sidebar avatar immediately
      try {
        const stored = JSON.parse(localStorage.getItem('outplay_user') || '{}');
        stored.profile_photo_url = res.data.photo_url;
        localStorage.setItem('outplay_user', JSON.stringify(stored));
        window.dispatchEvent(new CustomEvent('outplay:profile-photo-updated', { detail: { url: res.data.photo_url } }));
      } catch {}
      toast.success('Profile photo updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Phase 90.5 — Self-recovery for fees stuck "pending" after a completed Razorpay payment.
  // Most commonly: parent closes browser before our /verify-payment handler fires,
  // OR the academy hasn't configured the webhook URL on their Razorpay dashboard.
  // One click → backend queries Razorpay /v1/orders/{id}/payments → flips fee to paid.
  const [syncing, setSyncing] = useState(null);
  const handleSyncPayment = async (fee) => {
    setSyncing(fee.id);
    try {
      const r = await api.post(`/fees/${fee.id}/reconcile-payment`);
      const status = r.data?.status;
      if (status === 'reconciled' || status === 'already_paid') {
        toast.success(r.data?.message || 'Payment synced successfully!');
        // Refresh the fees list so the UI reflects paid status immediately.
        if (typeof fetchData === 'function') fetchData();
        else window.location.reload();
      } else if (status === 'pending_capture') {
        toast.warning(r.data?.message || 'Payment authorized but not yet captured by Razorpay. Try again in 1-2 minutes.');
      } else if (status === 'no_payment_found') {
        toast.error(r.data?.message || 'No Razorpay payment found for this order. Try paying again.');
      } else {
        toast.info(r.data?.message || 'Sync completed.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const handlePayFee = async (fee) => {
    setPaying(fee.id);    try {
      // Create order using academy's Razorpay keys
      const orderRes = await api.post(`/fees/${fee.id}/create-order`);
      const order = orderRes.data;
      
      // If already paid
      if (order.status === 'paid') {
        toast.info('Fee already paid!');
        fetchData();
        setPaying(null);
        return;
      }
      
      // Payment gateway not configured / fell back to mock — surface the
      // exact reason from the backend so the parent + academy admin can see
      // what to fix (Phase 90 diagnostic-first patch).
      if (order.mock_mode) {
        const reason = order.fallback_reason
          || 'Online payments are not yet enabled for your academy.';
        toast.error(
          `Payment Gateway Error: ${reason}. Please inform your academy administrator.`,
          { duration: 8000 }
        );
        setPaying(null);
        return;
      }
      
      // Real Razorpay checkout
      if (!window.Razorpay) { 
        toast.error('Payment gateway not loaded. Please refresh the page.'); 
        setPaying(null); 
        return; 
      }
      
      const rzp = new window.Razorpay({
        key: (order.key_id || '').trim(),
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.order_id,
        name: order.academy_name || 'Academy',
        description: order.description || `Fee Payment - ₹${(order.amount / 100).toLocaleString()}`,
        prefill: {
          name: profile?.name || user?.name || '',
          email: profile?.email || user?.email || '',
          contact: profile?.phone || ''
        },
        handler: async (response) => {
          try {
            await api.post(`/fees/${fee.id}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              payment_method: 'razorpay'
            });
            toast.success('✅ Payment successful! Receipt sent to your email.');
            fetchData();
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
          setPaying(null);
        },
        modal: {
          ondismiss: () => {
            toast.info('Payment cancelled');
            setPaying(null);
          }
        },
        theme: { color: '#0f766e' }
      });
      
      rzp.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description || 'Please try again'}`);
        setPaying(null);
      });
      
      rzp.open();
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed. Please try again.');
    }
    setPaying(null);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (pwdForm.new_password !== pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    setSavingPwd(true);
    try {
      await api.post('/auth/change-password', { current_password: pwdForm.current, new_password: pwdForm.new_password });
      toast.success('Password changed successfully');
      setShowResetPwd(false); setPwdForm({ current: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    finally { setSavingPwd(false); }
  };

  const handleViewReceipt = (feeId) => openSignedDocInNewTab(
    `/fees/${feeId}/view`,
    { label: 'Receipt', errorMessage: 'Failed' },
  );
  const handleDownloadReceipt = async (feeId) => {
    await downloadDocument(api, `/fees/${feeId}/download`, 'fee-receipt.pdf', { label: 'Fee Receipt' });
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-14 w-64" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div><Skeleton className="h-64 rounded-xl" /></div>;

  const stats = attendance?.stats || {};
  const records = attendance?.records || [];
  const pendingFees = fees.filter(f => f.status !== 'paid');
  const pendingAmount = pendingFees.reduce((s, f) => s + (f.amount || 0), 0);
  const p = profile || {};
  const name = p.name || user?.name || 'Student';

  // Calendar computation
  const monthRecords = records.filter(r => r.date?.startsWith(attMonth));
  const attPresent = monthRecords.filter(r => r.status === 'present').length;
  const attAbsent = monthRecords.filter(r => r.status === 'absent').length;
  const attLate = monthRecords.filter(r => r.status === 'late').length;
  const attTotal = monthRecords.length;
  const attRate = attTotal > 0 ? Math.round(attPresent / attTotal * 100) : (stats.rate || 0);

  const calendarDays = (() => {
    const [y, m] = attMonth.split('-').map(Number);
    const firstDay = new Date(y, m - 1, 1).getDay();
    const daysInMonth = new Date(y, m, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ day: d, dateStr });
    }
    return days;
  })();

  return (
    <div className="space-y-5" data-testid="student-portal">
      {/* Header with profile pic */}
      <div className="flex items-center gap-4 justify-between flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          <div className="relative group">
            {profilePhoto ? (
              <img src={profilePhoto} alt={name} className="h-16 w-16 rounded-full object-cover shadow-lg" />
            ) : (
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg text-white text-2xl font-bold">{name[0]}</div>
            )}
            <label className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer">
              <Camera className="h-5 w-5 text-white" />
              <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
            </label>
            {uploadingPhoto && <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center"><Loader2 className="h-5 w-5 text-white animate-spin" /></div>}
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold truncate">{name}</h1>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {p.email && <span className="text-sm text-muted-foreground">{p.email}</span>}
              <Badge variant="outline" className="text-xs font-mono">{p.custom_id || ''}</Badge>
              {p.course && <Badge className="text-xs bg-emerald-100 text-emerald-700 border-0">{p.course}</Badge>}
              {p.batch && <Badge className="text-xs bg-blue-100 text-blue-700 border-0">{p.batch}</Badge>}
            </div>
          </div>
        </div>
        {/* Phase 261h — Persistent "Get App" CTA for student/parent persona.
            Mobile-leaning copy ("Add to Home Screen") + dedicated source tag
            so PostHog can attribute installs to this surface separately.
            Phase 261i — gated by feature flag `show_pwa_student_cta`. */}
        {showGetAppCta && <DashboardGetAppButton source="student_portal_quick_action" />}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-emerald-50/60 dark:bg-emerald-950/10 border-emerald-200/50"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-emerald-600 font-medium">Attendance</span><CalendarCheck className="h-4 w-4 text-emerald-500" /></div>
          <p className="text-2xl font-display font-semibold tabular-nums">{stats.rate || 0}%</p>
          <p className="text-xs text-muted-foreground">{stats.present || 0}/{stats.total || 0} days</p>
        </CardContent></Card>
        <Card className="bg-amber-50/60 dark:bg-amber-950/10 border-amber-200/50"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-amber-600 font-medium">Streak</span><Flame className="h-4 w-4 text-amber-500" /></div>
          <p className="text-2xl font-display font-semibold tabular-nums">{stats.streak || 0}</p>
          <p className="text-xs text-muted-foreground">days in a row</p>
        </CardContent></Card>
        <Card className="bg-rose-50/60 dark:bg-rose-950/10 border-rose-200/50"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-rose-600 font-medium">Fees Due</span><IndianRupee className="h-4 w-4 text-rose-500" /></div>
          <p className="text-2xl font-display font-semibold tabular-nums text-rose-600">₹{pendingAmount.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">{pendingFees.length} pending</p>
        </CardContent></Card>
        <Card className="bg-purple-50/60 dark:bg-purple-950/10 border-purple-200/50"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-purple-600 font-medium">Progress</span><Award className="h-4 w-4 text-purple-500" /></div>
          <p className="text-2xl font-display font-semibold tabular-nums">{progressCards.length}</p>
          <p className="text-xs text-muted-foreground">assessments</p>
        </CardContent></Card>
      </div>

      {/* Phase 262 — Academy Challenge leaderboard.  Self-hides when no
          active challenge exists, so this is safe to mount unconditionally. */}
      <AcademyChallengeTile user={user} />

      {/* Phase 263 — Personal Records & Streaks.  Self-hides when the
          student has zero lifetime challenge submissions. */}
      <StudentStreakWidget />

      {/* Phase 326g — Claude Coach's Tip of the Day (personalised nudge). */}
      <CoachsTipCard />

      {/* 6 Sporty Tabs (Phase 65.7 — added Training & Events) */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full flex flex-wrap h-auto gap-1.5 bg-transparent p-0 justify-start">
          <TabsTrigger value="profile" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white bg-emerald-100 text-emerald-700 text-xs rounded-lg px-4"><User className="h-3.5 w-3.5 mr-1" /> My Profile</TabsTrigger>
          <TabsTrigger value="fees" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white bg-amber-100 text-amber-700 text-xs rounded-lg px-4"><IndianRupee className="h-3.5 w-3.5 mr-1" /> Fees</TabsTrigger>
          <TabsTrigger value="attendance" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white bg-blue-100 text-blue-700 text-xs rounded-lg px-4"><CalendarCheck className="h-3.5 w-3.5 mr-1" /> Attendance</TabsTrigger>
          <TabsTrigger value="progress" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white bg-purple-100 text-purple-700 text-xs rounded-lg px-4"><Award className="h-3.5 w-3.5 mr-1" /> Progress</TabsTrigger>
          <TabsTrigger value="training" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white bg-cyan-100 text-cyan-700 text-xs rounded-lg px-4" data-testid="student-tab-training"><Sparkles className="h-3.5 w-3.5 mr-1" /> Training</TabsTrigger>
        </TabsList>

        {/* My Profile - Professional Resume Style */}
        <TabsContent value="profile">
          <Card className="overflow-hidden" data-testid="student-portal-header">
            {/* Resume header with green accent */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-8 text-white">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
                {/* Profile photo */}
                <div className="relative group flex-shrink-0">
                  {profilePhoto ? (
                    <img src={profilePhoto} alt={name} className="h-28 w-28 rounded-full object-cover border-4 border-white/30 shadow-xl" />
                  ) : (
                    <div className="h-28 w-28 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/30 shadow-xl text-5xl font-bold">{name[0]}</div>
                  )}
                  <label className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer">
                    <Camera className="h-7 w-7 text-white" />
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                  </label>
                  {uploadingPhoto && <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center"><Loader2 className="h-7 w-7 text-white animate-spin" /></div>}
                </div>
                {/* Name + badges */}
                <div className="text-center sm:text-left flex-1">
                  <h2 className="text-2xl font-display font-bold">{name}</h2>
                  <p className="text-emerald-100 text-sm mt-0.5">{p.email || user?.email || ''}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2 justify-center sm:justify-start">
                    <Badge className="bg-white/20 border-white/30 text-white text-xs font-mono">{p.custom_id || ''}</Badge>
                    <Badge className="bg-white/20 border-white/30 text-white text-xs">{p.course || 'Football'}</Badge>
                    <Badge className="bg-white/20 border-white/30 text-white text-xs">{p.batch || 'Morning'}</Badge>
                    <Badge className="bg-emerald-400/30 border-emerald-300/30 text-white text-xs">{p.status || 'active'}</Badge>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium cursor-pointer transition-colors border border-white/20">
                    <Camera className="h-3.5 w-3.5" /> Upload Photo
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                  </label>
                  <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 text-xs" onClick={() => setShowResetPwd(true)}><Lock className="h-3.5 w-3.5 mr-1" /> Reset Password</Button>
                </div>
              </div>
            </div>

            <CardContent className="p-6">
              {/* Personal Information */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-emerald-700 uppercase tracking-wider mb-3 flex items-center gap-2"><User className="h-4 w-4" /> Personal Information</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                  {[
                    { label: 'Full Name', value: name },
                    { label: 'Email', value: p.email || user?.email || '-' },
                    { label: 'Phone', value: p.phone || '-' },
                    { label: 'Date of Birth', value: p.dob || '-' },
                    { label: 'Blood Group', value: p.blood_group || '-' },
                    { label: 'Address', value: p.address || '-' },
                  ].map(item => (
                    <div key={item.label} className="py-2 border-b border-border/50">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{item.label}</p>
                      <p className="text-sm font-medium mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Academy & Training */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wider mb-3 flex items-center gap-2"><Award className="h-4 w-4" /> Academy & Training</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                  {[
                    { label: 'Student ID', value: p.custom_id || '-' },
                    { label: 'Course', value: p.course || '-' },
                    { label: 'Batch', value: p.batch || '-' },
                    { label: 'Status', value: p.status || 'active' },
                    { label: 'Emergency Contact', value: p.emergency_contact || '-' },
                  ].map(item => (
                    <div key={item.label} className="py-2 border-b border-border/50">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{item.label}</p>
                      <p className="text-sm font-medium mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Guardian Information */}
              <div className="mb-6">
                <h3 className="text-sm font-bold text-purple-700 uppercase tracking-wider mb-3 flex items-center gap-2"><Users className="h-4 w-4" /> Guardian Information</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                  {[
                    { label: 'Guardian Name', value: p.guardian_name || '-' },
                    { label: 'Guardian Phone', value: p.guardian_phone || '-' },
                  ].map(item => (
                    <div key={item.label} className="py-2 border-b border-border/50">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{item.label}</p>
                      <p className="text-sm font-medium mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Physical Stats */}
              <div>
                <h3 className="text-sm font-bold text-amber-700 uppercase tracking-wider mb-3 flex items-center gap-2"><Ruler className="h-4 w-4" /> Physical Stats</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: 'Height', value: p.height ? `${p.height} cm` : '-', color: 'bg-blue-50 border-blue-200 text-blue-700' },
                    { label: 'Weight', value: p.weight ? `${p.weight} kg` : '-', color: 'bg-green-50 border-green-200 text-green-700' },
                    { label: 'BMI', value: p.bmi || '-', color: 'bg-purple-50 border-purple-200 text-purple-700' },
                    { label: 'Blood Group', value: p.blood_group || '-', color: 'bg-red-50 border-red-200 text-red-700' },
                  ].map(item => (
                    <div key={item.label} className={`text-center p-3 rounded-xl border ${item.color}`}>
                      <p className="text-[10px] uppercase tracking-wider opacity-70">{item.label}</p>
                      <p className="text-lg font-display font-bold mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fees */}
        <TabsContent value="fees">
          <div className="space-y-4">
            {/* Phase 249 — Self-service Year-End Fee Report download */}
            {profile?.id && (
              <YearEndReportCard mode="self" studentId={profile.id} />
            )}
            <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-28">Fee ID</TableHead><TableHead>Amount</TableHead><TableHead className="hidden md:table-cell">Type</TableHead><TableHead className="hidden md:table-cell">Due Date</TableHead><TableHead>Status</TableHead><TableHead className="w-36">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {fees.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No fees yet</TableCell></TableRow>
                : fees.map(fee => (
                  <TableRow key={fee.id}>
                    <TableCell><span className="text-xs font-mono text-primary">{fee.custom_id || fee.id.slice(0,8)}</span></TableCell>
                    <TableCell className="font-semibold tabular-nums">₹{(fee.amount||0).toLocaleString()}</TableCell>
                    <TableCell className="hidden md:table-cell"><Badge variant="outline" className="text-xs">{fee.fee_type}</Badge></TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{fee.due_date}</TableCell>
                    <TableCell><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${fee.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{fee.status === 'paid' ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />} {fee.status}</span></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="View" onClick={() => handleViewReceipt(fee.id)}><Eye className="h-3.5 w-3.5 text-blue-600" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Download" onClick={() => handleDownloadReceipt(fee.id)}><Download className="h-3.5 w-3.5 text-emerald-600" /></Button>
                        {fee.status !== 'paid' && (
                          <div className="flex items-center gap-1.5">
                            <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600" onClick={() => handlePayFee(fee)} disabled={paying === fee.id || syncing === fee.id} data-testid={`pay-fee-${fee.id}`}>
                              {paying === fee.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CreditCard className="h-3 w-3 mr-1" /> Pay Now</>}
                            </Button>
                            {/* Phase 90.5 — Self-recovery for completed-but-pending payments.
                                Only shown when an order_id exists (meaning a payment was
                                initiated previously) AND the fee is still pending. */}
                            {fee.order_id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-[10px] px-2 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                                onClick={() => handleSyncPayment(fee)}
                                disabled={paying === fee.id || syncing === fee.id}
                                title="Already paid but fee still shows pending? Click to sync from Razorpay."
                                data-testid={`sync-fee-${fee.id}`}
                              >
                                {syncing === fee.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" /> Sync</>}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
          </div>
        </TabsContent>

        {/* Attendance - Epic Calendar Dashboard */}
        <TabsContent value="attendance">
          <div className="space-y-4">
            {/* Month selector + Stats */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="bg-emerald-50/60 dark:bg-emerald-950/10 border-emerald-200/50">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-emerald-700">Monthly Summary</h3>
                    <select className="text-xs border rounded-lg px-2 py-1 bg-background" value={attMonth} onChange={e => setAttMonth(e.target.value)}>
                      {Array.from({length: 12}, (_, i) => { const d = new Date(2026, i); return <option key={i} value={`2026-${String(i+1).padStart(2,'0')}`}>{d.toLocaleString('default',{month:'long'})} 2026</option>; })}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-2 rounded-lg bg-white dark:bg-slate-900">
                      <p className="text-2xl font-display font-bold text-emerald-600 tabular-nums">{attPresent}</p>
                      <p className="text-[10px] text-muted-foreground">Present</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-white dark:bg-slate-900">
                      <p className="text-2xl font-display font-bold text-red-500 tabular-nums">{attAbsent}</p>
                      <p className="text-[10px] text-muted-foreground">Absent</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-white dark:bg-slate-900">
                      <p className="text-2xl font-display font-bold text-amber-500 tabular-nums">{attLate}</p>
                      <p className="text-[10px] text-muted-foreground">Late</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2"><Flame className="h-4 w-4 text-amber-500" /><span className="text-sm font-semibold">{stats.streak || 0} day streak</span></div>
                    <Badge className={`border-0 ${attRate >= 90 ? 'bg-emerald-100 text-emerald-700' : attRate >= 75 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{attRate}%</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* AI Insights */}
              <Card className="bg-blue-50/60 dark:bg-blue-950/10 border-blue-200/50">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3"><Award className="h-4 w-4 text-blue-600" /><h3 className="text-sm font-semibold text-blue-700">AI Insights</h3></div>
                  <div className="space-y-2.5">
                    {attRate >= 90 ? (
                      <>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-emerald-100/60 dark:bg-emerald-900/20"><CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-emerald-700">Excellent consistency! You're on track for the 95% target. Keep it up!</p></div>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-blue-100/60 dark:bg-blue-900/20"><Star className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-blue-700">Streak of {stats.streak || 0} days! You could be selected for team leadership.</p></div>
                      </>
                    ) : attRate >= 75 ? (
                      <>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-100/60 dark:bg-amber-900/20"><Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-amber-700">Good attendance! Focus on not missing consecutive days to build momentum.</p></div>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-blue-100/60 dark:bg-blue-900/20"><CalendarCheck className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-blue-700">Tip: Arrive 15 minutes early for warmup to maximize each session.</p></div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-red-100/60 dark:bg-red-900/20"><XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-red-700">Attendance needs improvement. Talk to your coach about a recovery plan.</p></div>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-100/60 dark:bg-amber-900/20"><Flame className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-amber-700">Set a goal: Attend every session this week to rebuild your streak.</p></div>
                      </>
                    )}
                    <div className="flex items-start gap-2 p-2 rounded-lg bg-purple-100/60 dark:bg-purple-900/20"><Award className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-purple-700">Coach recommendation: Regular attendance directly improves skill development.</p></div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Calendar Heatmap */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold">Attendance Calendar</h3>
                  <div className="flex items-center gap-3 text-[10px]">
                    <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-emerald-500" /> Present</span>
                    <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-red-400" /> Absent</span>
                    <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-amber-400" /> Late</span>
                    <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-muted" /> No session</span>
                  </div>
                </div>
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1.5">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="text-center text-[10px] font-semibold text-muted-foreground py-1">{d}</div>)}
                  {calendarDays.map((day, i) => {
                    if (!day) return <div key={`empty-${i}`} />;
                    const rec = monthRecords.find(r => r.date === day.dateStr);
                    const status = rec?.status;
                    const bgColor = status === 'present' ? 'bg-emerald-500 text-white' : status === 'absent' ? 'bg-red-400 text-white' : status === 'late' ? 'bg-amber-400 text-white' : 'bg-muted/40 text-muted-foreground';
                    return (
                      <div key={day.dateStr} className={`relative text-center py-1.5 rounded-lg text-xs font-medium cursor-default transition-all hover:ring-2 hover:ring-primary/30 ${bgColor}`} title={`${day.dateStr}: ${status || 'No session'}`}>
                        {day.day}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Records */}
            <Card>
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold mb-3">Recent Records</h3>
                <div className="space-y-2">
                  {monthRecords.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No records for this month</p> : monthRecords.slice(0, 10).map((r, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                      <span className="text-sm">{r.date}</span>
                      <Badge className={`text-xs border-0 ${r.status === 'present' ? 'bg-emerald-100 text-emerald-700' : r.status === 'late' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {r.status === 'present' ? <CheckCircle className="h-3 w-3 mr-1" /> : r.status === 'late' ? <Clock className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}{r.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Progress - Epic Dashboard */}
        <TabsContent value="progress">
          <div className="space-y-4">
            {/* Stats Row */}
            {progressCards.length > 0 && (() => {
              const allRatings = progressCards.flatMap(c => Object.values(c.skills_rating || {}));
              const avg = allRatings.length > 0 ? (allRatings.reduce((a,b) => a+b, 0) / allRatings.length).toFixed(1) : 0;
              const highest = allRatings.length > 0 ? Math.max(...allRatings).toFixed(1) : 0;
              const lowest = allRatings.length > 0 ? Math.min(...allRatings).toFixed(1) : 0;
              const bestSkill = progressCards[0]?.skills_rating ? Object.entries(progressCards[0].skills_rating).sort((a,b) => b[1]-a[1])[0] : null;
              const weakSkill = progressCards[0]?.skills_rating ? Object.entries(progressCards[0].skills_rating).sort((a,b) => a[1]-b[1])[0] : null;
              const overallPct = avg ? Math.round(avg / 5 * 100) : 0;

              return (
                <>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Card className="bg-purple-50/60 dark:bg-purple-950/10 border-purple-200/50"><CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2"><span className="text-xs text-purple-600 font-medium">Total Cards</span><Award className="h-4 w-4 text-purple-500" /></div>
                      <p className="text-2xl font-display font-bold tabular-nums">{progressCards.length}</p>
                      <p className="text-xs text-muted-foreground">Overall: {avg}/5 ({overallPct}%)</p>
                    </CardContent></Card>
                    <Card className="bg-emerald-50/60 dark:bg-emerald-950/10 border-emerald-200/50"><CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2"><span className="text-xs text-emerald-600 font-medium">Best Skill</span><Star className="h-4 w-4 text-emerald-500" /></div>
                      <p className="text-2xl font-display font-bold tabular-nums text-emerald-600">{highest}/5</p>
                      <p className="text-xs text-muted-foreground capitalize">{bestSkill ? bestSkill[0] : '-'}</p>
                    </CardContent></Card>
                    <Card className="bg-amber-50/60 dark:bg-amber-950/10 border-amber-200/50"><CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2"><span className="text-xs text-amber-600 font-medium">Needs Work</span><Flame className="h-4 w-4 text-amber-500" /></div>
                      <p className="text-2xl font-display font-bold tabular-nums text-amber-600">{lowest}/5</p>
                      <p className="text-xs text-muted-foreground capitalize">{weakSkill ? weakSkill[0] : '-'}</p>
                    </CardContent></Card>
                  </div>

                  {/* AI Insights */}
                  <Card className="bg-blue-50/60 dark:bg-blue-950/10 border-blue-200/50"><CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2"><Award className="h-4 w-4 text-blue-600" /><h3 className="text-sm font-semibold text-blue-700">AI Performance Insights</h3></div>
                    <div className="grid sm:grid-cols-3 gap-2">
                      {overallPct >= 80 ? (
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-emerald-100/60"><CheckCircle className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-emerald-700">Excellent all-rounder! Keep pushing for elite level.</p></div>
                      ) : overallPct >= 60 ? (
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-100/60"><Clock className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-amber-700">Good foundation. Focus on weaker skills daily.</p></div>
                      ) : (
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-red-100/60"><XCircle className="h-3.5 w-3.5 text-red-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-red-700">More practice needed. Work with your coach on basics.</p></div>
                      )}
                      {weakSkill && <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-100/60"><Flame className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-amber-700">Focus area: <strong className="capitalize">{weakSkill[0]}</strong> ({weakSkill[1]}/5). Practice daily drills.</p></div>}
                      {bestSkill && <div className="flex items-start gap-2 p-2 rounded-lg bg-emerald-100/60"><Star className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" /><p className="text-xs text-emerald-700">Strength: <strong className="capitalize">{bestSkill[0]}</strong> ({bestSkill[1]}/5). Use this to lead team plays!</p></div>}
                    </div>
                  </CardContent></Card>
                </>
              );
            })()}

            {/* Card Selector + Details */}
            {progressCards.length === 0 ? (
              <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-16"><Award className="h-12 w-12 text-muted-foreground/40 mb-4" /><h3 className="text-lg font-semibold mb-1">No progress cards yet</h3><p className="text-sm text-muted-foreground">Your coach will add skill assessments here.</p></CardContent></Card>
            ) : (
              <>
                {/* Timeline selector */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {progressCards.map((card, i) => {
                    const cardAvg = card.skills_rating ? (Object.values(card.skills_rating).reduce((a,b) => a+b, 0) / Object.values(card.skills_rating).length).toFixed(1) : 0;
                    const isSelected = (selectedCard || 0) === i;
                    return (
                      <button key={card.id} onClick={() => setSelectedCard(i)} className={`flex-shrink-0 p-3 rounded-xl border-2 text-left transition-all min-w-[160px] ${isSelected ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/20 shadow-lg shadow-purple-500/10' : 'border-border hover:border-purple-300 hover:shadow-md'}`}>
                        <p className="text-[10px] text-muted-foreground">{card.created_at?.slice(0, 10)}</p>
                        <p className="text-xs font-semibold mt-0.5">{card.coach_name || 'Coach'}</p>
                        <p className="text-lg font-display font-bold tabular-nums mt-1">{cardAvg}<span className="text-xs text-muted-foreground">/5</span></p>
                      </button>
                    );
                  })}
                </div>

                {/* Selected Card Detail - NEW Digital Athlete ID */}
                <DigitalAthleteCard 
                  student={profile}
                  progressCard={progressCards[selectedCard || 0]}
                />

                {/* Phase 197-H — AI Coach's Training Blueprint */}
                <StudentAIRecommendationCard studentId={profile?.id} />

                {/* Phase 197.b — Trend AI longitudinal growth trajectory */}
                <AthleteTrajectoryCard studentId={profile?.id} />
              </>
            )}
          </div>
        </TabsContent>

        {/* Phase 65.7 — Training Sessions */}
        <TabsContent value="training" className="space-y-3">
          {trainingSessions === null ? (
            <Skeleton className="h-32 w-full rounded-lg" />
          ) : trainingSessions.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No upcoming training sessions for your batch yet.</CardContent></Card>
          ) : trainingSessions.map((s) => (
            <Card key={s.id} data-testid={`student-training-${s.id}`} className="border-l-4 border-l-cyan-500">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-display font-semibold text-sm break-words">{s.title}</p>
                    <p className="text-xs text-muted-foreground">{s.coach_name || 'Coach'}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">{s.status || 'planned'}</Badge>
                </div>
                <div className="flex flex-wrap gap-1">
                  {s.course && <Badge variant="outline" className="text-[10px]">{s.course}</Badge>}
                  {(Array.isArray(s.batches) && s.batches.length > 0 ? s.batches : (s.batch ? [s.batch] : [])).map(b => <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>)}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><CalendarIcon className="h-3.5 w-3.5" /> {s.date} · {s.start_time} ({s.duration_minutes} min)</div>
                {((Array.isArray(s.locations) && s.locations.length > 0) || s.location) && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="break-words">{(Array.isArray(s.locations) && s.locations.length > 0 ? s.locations : [s.location]).filter(Boolean).join(' · ')}</span>
                  </div>
                )}
                {Array.isArray(s.drills) && s.drills.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {s.drills.map((d, i) => <Badge key={i} variant="outline" className="text-[10px] bg-cyan-50 text-cyan-700 border-cyan-200">{d}</Badge>)}
                  </div>
                )}
                {s.notes && <p className="text-xs">{s.notes}</p>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Phase 266c — Household invite (silent if no academy slug on user) */}
      <div className="mt-6">
        <HouseholdInviteLink
          academyName={user?.academy_name || profile?.academy_name}
          studentName={profile?.name || user?.name}
        />
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPwd} onOpenChange={setShowResetPwd}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-primary" /> Reset Password</DialogTitle></DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Current Password</Label><Input type="password" value={pwdForm.current} onChange={e => setPwdForm({...pwdForm, current: e.target.value})} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">New Password</Label><Input type="password" value={pwdForm.new_password} onChange={e => setPwdForm({...pwdForm, new_password: e.target.value})} placeholder="Min 6 characters" required /></div>
            <div className="space-y-1.5"><Label className="text-xs">Confirm New Password</Label><Input type="password" value={pwdForm.confirm} onChange={e => setPwdForm({...pwdForm, confirm: e.target.value})} required /></div>
            <Button type="submit" className="w-full" disabled={savingPwd}>{savingPwd ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}{savingPwd ? 'Changing...' : 'Change Password'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
